// What `/demo` is, in this app.
//
// `src/core/demo.js` is the chassis's answer and it stays as it is: it knows
// the URL, and it knows that demo data must never reach real storage. This
// file is the app's answer to the question the chassis does not ask — WHERE
// THE DATA COMES FROM.
//
// It comes from PTV. A departure board full of invented trains is the one
// screenshot this app must not take: somebody deciding whether to install is
// shown times that were never timetabled, on a line whose real status they
// could check from the platform they are standing on. So `/demo` is the real
// app, reading the real endpoints, pre-seeded with the lines, stations and
// commute rule a person would have set up on their first evening — and saving
// none of it.
//
// THE ONE EXCEPTION is below.

import { computed, ref } from 'vue';
import { isDemo } from './core/demo.js';

/**
 * Read the `fixture` query parameter — the escape hatch for reproducible
 * screenshots.
 *
 * `/demo?fixture=sample` puts the deterministic sample data back, end to end:
 * the board, the run patterns, the live trains and the disruption feed all
 * come from the `sample*` functions in `src/departures.js` and `src/ptv.js`
 * rather than from the network. Store screenshots have to be reproducible —
 * a listing image is compared against the last one, and "the 8:14 is four
 * minutes late today" is not a difference anybody wants to review — and a
 * screenshot run cannot depend on PTV being up, on the deploy holding
 * credentials, or on there being a train at all at the hour CI happens to run.
 *
 * Plain `/demo` is live. The fixture is opt-in, by URL, and nothing links to it.
 */
function fixtureParam() {
  try {
    return new URLSearchParams(location.search).get('fixture');
  } catch {
    return null;   // no location (SSR, the test runner), no fixture
  }
}

/** Is this a QA screenshot run? The harness sets the flag before any app code. */
function isQaRun() {
  return typeof window !== 'undefined' && !!window.__APPFACTORY_QA__;
}

/**
 * True when the sample data is what the screens should show.
 *
 * `/demo?fixture=sample`, and every QA run. A QA run gets it WITHOUT the query
 * parameter on purpose: the screenshot harness navigates to URLs it composes
 * itself, it runs in a container that may have no route to PTV at all, and
 * every one of its fixtures is an assertion about how a screen looks rather
 * than about what the trains are doing. A deterministic answer is the only
 * kind it can audit.
 *
 * A REF, not a constant, so a test can turn it on and off around the modules
 * that read it. Nothing in the app writes it.
 */
export const sampleData = ref(isDemo.value && (fixtureParam() === 'sample' || isQaRun()));

/** `/demo` reading live data — the default, and the thing this app ships. */
export const liveDemo = computed(() => isDemo.value && !sampleData.value);
